import React, { useState } from 'react';
import { FileText, Download, Calendar, Users, TrendingUp, DollarSign, Loader2, RefreshCw, BarChart3, FileText as FileTextIcon, Printer } from 'lucide-react';
import { Member, Contribution, Event, Expense, Town } from '../../types';
import { useGlobalToast } from '../../contexts/ToastContext';
import { exportMembersReport, exportFinancialReport, exportExpenseReport, exportEventReport } from '../../utils/excel';
import { exportMembersReportPDF, exportFinancialReportPDF, exportExpenseReportPDF, exportEventReportPDF } from '../../utils/pdfExport';
import { darkModeClasses, getReportCardClasses, getInfoPanelClasses } from '../../utils/darkMode';
import { useAuth } from '../../contexts/AuthContext';
import { MemberDemographicsChart } from './MemberDemographicsChart';
import { FinancialSummaryChart } from './FinancialSummaryChart';
import { ActivityReportChart } from './ActivityReportChart';
import { HelpButton } from '../Help/HelpButton';

interface ReportCenterProps {
  members: Member[];
  contributions: Contribution[];
  events: Event[];
  expenses: Expense[];
  towns: Town[];
  selectedTownId: string;
  activeReportType: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const ReportCenter: React.FC<ReportCenterProps> = ({
  members,
  contributions,
  events,
  expenses,
  towns,
  selectedTownId,
  activeReportType,
  onRefresh,
  isRefreshing = false
}) => {
  useGlobalToast();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState('');
  const [generatingFormat, setGeneratingFormat] = useState<'excel' | 'pdf' | null>(null);
  const [viewType, setViewType] = useState<'data' | 'charts'>('data');
  const [chartType, setChartType] = useState<'pie' | 'bar' | 'line' | 'area'>('line');
  const [timeRange, setTimeRange] = useState<'monthly' | 'weekly' | 'daily'>('monthly');

  useAuth();

  const selectedTownName = (() => {
    if (selectedTownId === 'no-town-assigned') {
      return 'No Town Assigned';
    }
    if (selectedTownId === 'all') {
      return 'All Towns';
    }
    return towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';
  })();

  const generateReport = async (reportType: string, format: 'excel' | 'pdf', action: () => void) => {
    setIsGenerating(true);
    setGeneratingType(reportType);
    setGeneratingFormat(format);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      action();
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsGenerating(false);
      setGeneratingType('');
      setGeneratingFormat(null);
    }
  };

  const handleExpenseReport = (format: 'excel' | 'pdf') => {
    if (format === 'excel') {
      exportExpenseReport(expenses, towns, selectedTownId, dateRange);
    } else {
      exportExpenseReportPDF(expenses, towns, selectedTownId, dateRange);
    }
  };

  const handleEventReport = (format: 'excel' | 'pdf') => {
    if (format === 'excel') {
      exportEventReport(events, towns, selectedTownId, dateRange);
    } else {
      exportEventReportPDF(events, towns, selectedTownId, dateRange);
    }
  };

  const reportTypes = [
    {
      id: 'member-reports',
      title: 'Member Reports',
      description: 'Export comprehensive member data with registration details and contribution history',
      icon: Users,
      color: 'blue',
      action: (format: 'excel' | 'pdf') => {
        if (format === 'excel') {
          exportMembersReport(members, towns, selectedTownId);
        } else {
          exportMembersReportPDF(members, towns, selectedTownId);
        }
      }
    },
    {
      id: 'financial-reports',
      title: 'Financial Reports',
      description: 'Combined income and expense reports with detailed transaction history',
      icon: TrendingUp,
      color: 'green',
      action: (format: 'excel' | 'pdf') => {
        const filteredContributions = contributions.filter(c => {
          const date = new Date(c.date);
          return date >= new Date(dateRange.startDate) && date <= new Date(dateRange.endDate);
        });
        const filteredExpenses = expenses.filter(e => {
          const date = new Date(e.date);
          return date >= new Date(dateRange.startDate) && date <= new Date(dateRange.endDate);
        });
        if (format === 'excel') {
          exportFinancialReport(filteredContributions, filteredExpenses, towns, selectedTownId);
        } else {
          exportFinancialReportPDF(filteredContributions, filteredExpenses, towns, selectedTownId);
        }
      }
    },
    {
      id: 'expense-reports',
      title: 'Expense Reports',
      description: 'Detailed expense tracking by category and beneficiary',
      icon: DollarSign,
      color: 'red',
      action: handleExpenseReport
    },
    {
      id: 'event-reports',
      title: 'Event Reports',
      description: 'Event attendance and participation tracking with member details',
      icon: Calendar,
      color: 'purple',
      action: handleEventReport
    }
  ];

  const getActiveReport = () => {
    return reportTypes.find(report => report.id === activeReportType) || reportTypes[0];
  };

  const activeReport = getActiveReport();


  const getStats = () => {
    const filteredMembers = selectedTownId === 'all' ? members : members.filter(m => m.townId === selectedTownId);
    const filteredContributions = selectedTownId === 'all' ? contributions : contributions.filter(c => c.townId === selectedTownId);
    const filteredEvents = selectedTownId === 'all' ? events : events.filter(e => e.townId === selectedTownId);
    const filteredExpenses = selectedTownId === 'all' ? expenses : expenses.filter(e => e.townId === selectedTownId);

    return {
      totalMembers: filteredMembers.length,
      totalContributions: filteredContributions.reduce((sum, c) => sum + c.amount, 0),
      totalEvents: filteredEvents.length,
      totalExpenses: filteredExpenses.reduce((sum, e) => sum + e.amount, 0)
    };
  };

  const stats = getStats();

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className={`text-xl md:text-2xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <FileText className="h-6 w-6 md:h-7 md:w-7 mr-2 md:mr-3 text-blue-600" />
            <span className="hidden sm:inline">{activeReport.title} - </span>{selectedTownName}
            <HelpButton guideId="reports/report-center" variant="icon" size="sm" className="ml-2" />
          </h1>
          <p className={`${darkModeClasses.textSecondary} mt-1`}>{activeReport.description}</p>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${darkModeClasses.card} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkModeClasses.textSecondary}`}>Total Members</p>
              <p className={`text-2xl font-bold ${darkModeClasses.textPrimary}`}>{stats.totalMembers}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className={`${darkModeClasses.card} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkModeClasses.textSecondary}`}>Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">GHS {stats.totalContributions.toFixed(2)}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className={`${darkModeClasses.card} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkModeClasses.textSecondary}`}>Total Events</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalEvents}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className={`${darkModeClasses.card} p-4 rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkModeClasses.textSecondary}`}>Total Expenses</p>
              <p className="text-2xl font-bold text-red-600">GHS {stats.totalExpenses.toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className={`${darkModeClasses.container} p-4`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary}`}>View Options</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setViewType('data')}
                className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  viewType === 'data'
                    ? 'bg-blue-600 text-white'
                    : `${darkModeClasses.card} ${darkModeClasses.textSecondary} hover:${darkModeClasses.hover}`
                }`}
              >
                <FileText className="h-4 w-4 inline mr-1" />
                Data View
              </button>
              <button
                onClick={() => setViewType('charts')}
                className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  viewType === 'charts'
                    ? 'bg-blue-600 text-white'
                    : `${darkModeClasses.card} ${darkModeClasses.textSecondary} hover:${darkModeClasses.hover}`
                }`}
              >
                <BarChart3 className="h-4 w-4 inline mr-1" />
                Chart View
              </button>
            </div>

            {viewType === 'charts' && (
              <>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as 'pie' | 'bar' | 'line' | 'area')}
                  className={`${darkModeClasses.input} rounded px-3 py-2 text-sm`}
                  aria-label="Chart Type"
                >
                  <option value="line">Line Chart</option>
                  <option value="bar">Bar Chart</option>
                  <option value="area">Area Chart</option>
                  <option value="pie">Pie Chart</option>
                </select>

                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value as 'monthly' | 'weekly' | 'daily')}
                  className={`${darkModeClasses.input} rounded px-3 py-2 text-sm`}
                  aria-label="Time Range"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {viewType === 'charts' && (
        <div className="space-y-6">
          {/* Member Demographics Chart */}
          <div className={`${darkModeClasses.container} p-6`}>
            <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4 flex items-center`}>
              <Users className="h-5 w-5 mr-2 text-blue-600" />
              Member Demographics
            </h3>
            <MemberDemographicsChart
              members={stats.totalMembers > 0 ? members : []}
              chartType={chartType === 'pie' ? 'pie' : 'bar'}
              height={300}
            />
          </div>

          {/* Financial Summary Chart */}
          <div className={`${darkModeClasses.container} p-6`}>
            <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4 flex items-center`}>
              <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
              Financial Summary
            </h3>
            <FinancialSummaryChart
              contributions={contributions}
              expenses={expenses}
              chartType={chartType === 'area' ? 'area' : chartType === 'bar' ? 'bar' : 'line'}
              timeRange={timeRange}
              height={300}
            />
          </div>

          {/* Activity Report Chart */}
          <div className={`${darkModeClasses.container} p-6`}>
            <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4 flex items-center`}>
              <Calendar className="h-5 w-5 mr-2 text-purple-600" />
              Activity Trends
            </h3>
            <ActivityReportChart
              contributions={contributions}
              events={events}
              chartType={chartType === 'area' ? 'area' : chartType === 'bar' ? 'bar' : 'line'}
              timeRange={timeRange}
              height={300}
              showBoth={true}
            />
          </div>
        </div>
      )}

      {/* Data Export Section - Only show when in data view */}
      {viewType === 'data' && (
        <div className={`${darkModeClasses.container} p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary}`}>Generate Reports</h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2">
                <label htmlFor="startDate" className={`text-sm ${darkModeClasses.textSecondary} whitespace-nowrap`}>Start Date:</label>
                <input
                  id="startDate"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                  className={`${darkModeClasses.input} rounded px-2 py-1 text-sm w-full xs:w-auto`}
                />
                <label htmlFor="endDate" className={`text-sm ${darkModeClasses.textSecondary} whitespace-nowrap xs:ml-2`}>End Date:</label>
                <input
                  id="endDate"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                  className={`${darkModeClasses.input} rounded px-2 py-1 text-sm w-full xs:w-auto`}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {reportTypes.map((report) => (
              <div
                key={report.id}
                className={`p-4 border-2 rounded-lg ${getReportCardClasses(report.color, activeReportType === report.id)}`}
              >
                <div className="flex items-start space-x-3 mb-3">
                  <report.icon className="h-6 w-6 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold break-words">{report.title}</h3>
                    <p className={`text-sm ${darkModeClasses.textSecondary} mt-1 break-words`}>{report.description}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => generateReport(report.id, 'excel', () => report.action('excel'))}
                    disabled={isGenerating}
                    className={`flex-1 ${darkModeClasses.card} rounded-lg px-3 py-2 text-sm font-medium ${darkModeClasses.hover} disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2`}
                  >
                    {isGenerating && generatingType === report.id && generatingFormat === 'excel' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        <span>Export Excel</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => generateReport(report.id, 'pdf', () => report.action('pdf'))}
                    disabled={isGenerating}
                    className={`flex-1 ${darkModeClasses.card} rounded-lg px-3 py-2 text-sm font-medium ${darkModeClasses.hover} disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2`}
                  >
                    {isGenerating && generatingType === report.id && generatingFormat === 'pdf' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FileTextIcon className="h-4 w-4" />
                        <span>Export PDF</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`${getInfoPanelClasses('blue')} border rounded-lg p-4`}>
        <div className="flex items-center space-x-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h3 className="font-medium">Report Information</h3>
        </div>
        <div className="mt-2 text-sm">
          <p>• Reports are automatically filtered based on your selected town/branch</p>
          <p>• "All Towns" selection generates consolidated reports across all locations</p>
          <p>• Excel files include professional formatting, charts, and calculations</p>
          <p>• PDF files include professional templates with charts and detailed formatting</p>
          <p>• Date range filtering applies to all transaction-based reports</p>
          <p>• Switch between Data View and Chart View to see visual analytics</p>
          <p>• Export both Excel and PDF formats for maximum compatibility</p>
        </div>
      </div>
    </div>
  );
};